import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useCollections } from '../hooks/useCollections';

export default function FundsAdmin() {
  const queryClient = useQueryClient();
  const { data: items = [], isLoading, error } = useCollections({ includeInactive: true });
  const [createOpen, setCreateOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState('');
  const [formError, setFormError] = useState('');

  const invalidate = () => queryClient.invalidateQueries(['collections']);

  const createMutation = useMutation(
    (payload) => axios.post('/api/collections', payload),
    {
      onSuccess: () => {
        invalidate();
        setCreateOpen(false);
        setCode('');
        setName('');
        setFormError('');
      },
      onError: (err) => setFormError(err.response?.data?.error || 'Не удалось создать фонд'),
    }
  );

  const updateMutation = useMutation(
    ({ code: itemCode, payload }) => axios.put(`/api/collections/${encodeURIComponent(itemCode)}`, payload),
    {
      onSuccess: () => {
        invalidate();
        setEditItem(null);
        setFormError('');
      },
      onError: (err) => setFormError(err.response?.data?.error || 'Не удалось сохранить фонд'),
    }
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Фонды</Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Создать фонд
        </Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>Не удалось загрузить фонды</Alert>}
      {formError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFormError('')}>{formError}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Код</TableCell>
              <TableCell>Название</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.code}>
                <TableCell>{item.code}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>
                  <Chip size="small" label={item.is_active ? 'активен' : 'неактивен'} color={item.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => { setEditItem(item); setEditName(item.name); }}>
                    Переименовать
                  </Button>
                  <Switch
                    checked={Boolean(item.is_active)}
                    onChange={(e) => updateMutation.mutate({ code: item.code, payload: { is_active: e.target.checked } })}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Новый фонд</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Код" fullWidth required value={code} onChange={(e) => setCode(e.target.value)} />
          <TextField margin="dense" label="Название" fullWidth required value={name} onChange={(e) => setName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={() => createMutation.mutate({ code, name })} disabled={createMutation.isLoading}>
            Создать
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editItem)} onClose={() => setEditItem(null)} fullWidth maxWidth="xs">
        <DialogTitle>Переименовать фонд</DialogTitle>
        <DialogContent>
          <TextField margin="dense" label="Название" fullWidth required value={editName} onChange={(e) => setEditName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => updateMutation.mutate({ code: editItem.code, payload: { name: editName } })}
            disabled={updateMutation.isLoading}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
